import { motion } from 'framer-motion';
import { useState } from 'react';
import { Mail, Phone, MapPin } from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const fadeInUp = {
  initial: { opacity: 0, y: 40 },
  whileInView: { opacity: 1, y: 0 },
  transition: { duration: 0.8, ease: [0.22, 1, 0.36, 1] },
  viewport: { once: true, margin: '-100px' },
};

const ContactPage = () => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    message: '',
  });
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);

    try {
      const response = await axios.post(`${API}/contact`, formData);
      toast.success(response.data.message || 'Message sent successfully!');
      setFormData({
        name: '',
        email: '',
        phone: '',
        message: '',
      });
    } catch (error) {
      console.error('Error submitting form:', error);
      toast.error(error.response?.data?.detail || 'Failed to send message. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div data-testid="contact-page" className="pt-32 pb-24 bg-[#F2F0E9]">
      <div className="max-w-[1800px] mx-auto px-6 md:px-12 lg:px-24">
        {/* Header */}
        <motion.div {...fadeInUp} className="text-center mb-24">
          <h1 className="hero-title text-[#1A1A1A] mb-6 accent-yellow">
            Get in Touch
          </h1>
          <p className="text-lg md:text-xl text-[#595959] max-w-3xl mx-auto leading-relaxed">
            Let's discuss how we can elevate your business with our premium solutions.
          </p>
        </motion.div>

        {/* Split Screen Layout */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 lg:gap-24">
          {/* Contact Information */}
          <motion.div
            {...fadeInUp}
            data-testid="contact-info"
            className="space-y-12"
          >
            <div>
              <h2 className="serif text-3xl text-[#1A1A1A] mb-8">
                Quality Services
              </h2>
              <p className="text-base text-[#595959] leading-relaxed mb-12">
                We're here to answer your questions and discuss your requirements. Reach out to us through any of these channels.
              </p>
            </div>

            <div className="space-y-8">
              <div className="flex items-start gap-4">
                <Phone size={24} className="text-[#1A1A1A] mt-1" strokeWidth={1} />
                <div>
                  <p className="text-xs uppercase tracking-widest text-[#595959] mb-2">
                    Phone
                  </p>
                  <p className="text-lg text-[#1A1A1A]">
                    +91 98153 30800<br />
                    +91 98153 30801
                  </p>
                </div>
              </div>

              <div className="flex items-start gap-4">
                <Mail size={24} className="text-[#1A1A1A] mt-1" strokeWidth={1} />
                <div>
                  <p className="text-xs uppercase tracking-widest text-[#595959] mb-2">
                    Email
                  </p>
                  <p className="text-lg text-[#1A1A1A]">
                    qualityasr@gmail.com
                  </p>
                </div>
              </div>

              <div className="flex items-start gap-4">
                <MapPin size={24} className="text-[#1A1A1A] mt-1" strokeWidth={1} />
                <div>
                  <p className="text-xs uppercase tracking-widest text-[#595959] mb-2">
                    Address
                  </p>
                  <p className="text-lg text-[#1A1A1A] leading-relaxed">
                    #119, Second Floor, Super Plaza Market<br />
                    Cooper Road, Amritsar 143001<br />
                    Punjab, India
                  </p>
                </div>
              </div>
            </div>

            <div className="pt-8">
              <p className="cinzel text-sm uppercase tracking-widest text-[#595959]">
                Business Hours
              </p>
              <p className="text-base text-[#1A1A1A] mt-2">
                Monday - Saturday: 10:00 AM - 6:00 PM<br />
                Sunday: Closed
              </p>
            </div>
          </motion.div>

          {/* Contact Form */}
          <motion.div
            {...fadeInUp}
            data-testid="contact-form-container"
            className="bg-white p-8 md:p-12 border border-[#D6D3C9]"
          >
            <form onSubmit={handleSubmit} className="space-y-8">
              <div>
                <label
                  htmlFor="name"
                  className="block text-xs uppercase tracking-widest text-[#595959] mb-3"
                >
                  Full Name
                </label>
                <input
                  type="text"
                  id="name"
                  name="name"
                  data-testid="contact-form-name"
                  value={formData.name}
                  onChange={handleChange}
                  required
                  className="w-full bg-transparent border-b border-[#D6D3C9] py-3 text-base text-[#1A1A1A] focus:outline-none focus:border-[#1A1A1A] transition-colors duration-300"
                  placeholder="Your name"
                />
              </div>

              <div>
                <label
                  htmlFor="email"
                  className="block text-xs uppercase tracking-widest text-[#595959] mb-3"
                >
                  Email Address
                </label>
                <input
                  type="email"
                  id="email"
                  name="email"
                  data-testid="contact-form-email"
                  value={formData.email}
                  onChange={handleChange}
                  required
                  className="w-full bg-transparent border-b border-[#D6D3C9] py-3 text-base text-[#1A1A1A] focus:outline-none focus:border-[#1A1A1A] transition-colors duration-300"
                  placeholder="your@email.com"
                />
              </div>

              <div>
                <label
                  htmlFor="phone"
                  className="block text-xs uppercase tracking-widest text-[#595959] mb-3"
                >
                  Phone Number
                </label>
                <input
                  type="tel"
                  id="phone"
                  name="phone"
                  data-testid="contact-form-phone"
                  value={formData.phone}
                  onChange={handleChange}
                  required
                  className="w-full bg-transparent border-b border-[#D6D3C9] py-3 text-base text-[#1A1A1A] focus:outline-none focus:border-[#1A1A1A] transition-colors duration-300"
                  placeholder="+91 XXXXX XXXXX"
                />
              </div>

              <div>
                <label
                  htmlFor="message"
                  className="block text-xs uppercase tracking-widest text-[#595959] mb-3"
                >
                  Message
                </label>
                <textarea
                  id="message"
                  name="message"
                  data-testid="contact-form-message"
                  value={formData.message}
                  onChange={handleChange}
                  required
                  rows={6}
                  className="w-full bg-transparent border-b border-[#D6D3C9] py-3 text-base text-[#1A1A1A] focus:outline-none focus:border-[#1A1A1A] transition-colors duration-300 resize-none"
                  placeholder="Tell us about your requirements..."
                />
              </div>

              <button
                type="submit"
                data-testid="contact-form-submit"
                disabled={isSubmitting}
                className="luxury-button w-full disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {isSubmitting ? 'Sending...' : 'Send Message'}
              </button>
            </form>
          </motion.div>
        </div>
      </div>
    </div>
  );
};

export default ContactPage;
